import type { Messages } from './types';

/**
 * Arabic. Plural messages carry all six CLDR categories, because Arabic uses
 * them: zero, one, two, few (3–10), many (11–99) and other.
 */
export const ar: Messages = {
  'app.tagline': 'خمس صلوات، كل يوم، محفوظة معًا.',
  'app.blurb': 'احفظ سلسلتك في الصلاة، وفي وقتها، وفي الجماعة، وإدراك التكبيرة الأولى.',
  'app.streakLabel': 'سلسلة الصلاة',
  'app.streakOwner': 'سلسلة {{name}}',
  'app.provisionalName': 'اسم مؤقت — غير نهائي',

  'welcome.start.kids': 'بسم الله — لنبدأ',
  'welcome.start.adult': 'ابدأ',
  'welcome.privacy': 'كل شيء يبقى على هذا الهاتف. لا يُرسل إلى أي مكان.',

  'onboarding.title': 'من يصلي؟',
  'onboarding.nameLabel': 'الاسم',
  'onboarding.namePlaceholder': 'اسمك',
  'onboarding.ageLabel': 'كم عمرك؟',
  'onboarding.years': {
    zero: '{{count}} سنة',
    one: 'سنة واحدة',
    two: 'سنتان',
    few: '{{count}} سنوات',
    many: '{{count}} سنة',
    other: '{{count}} سنة',
  },
  'onboarding.yearsUnit': {
    zero: 'سنة',
    one: 'سنة',
    two: 'سنتان',
    few: 'سنوات',
    many: 'سنة',
    other: 'سنة',
  },
  'onboarding.agePlus': '{{age}}+',
  'onboarding.yearsPlus': '{{age}}+ سنة',
  'onboarding.younger': 'أصغر',
  'onboarding.older': 'أكبر',
  'onboarding.continue': 'متابعة',
  'onboarding.privacy': 'يبقى هذا على الهاتف. يكفي اسم مستعار.',
  'onboarding.verdict.kids': 'يحصل {{who}} على النسخة الممتعة',
  'onboarding.verdict.adult': 'يحصل {{who}} على النسخة البسيطة',
  'onboarding.verdictNote.kids': 'فوانيس وألوان ومكافآت — {{age}} فأقل',
  'onboarding.verdictNote.adult': 'السجل نفسه بلا طبقة لعب — {{age}} فأكثر',

  'home.heroEyebrow': 'صلوات متتالية',
  'home.prayersUnit': {
    zero: 'صلاة',
    one: 'صلاة',
    two: 'صلاتان',
    few: 'صلوات',
    many: 'صلاة',
    other: 'صلاة',
  },
  'home.days': {
    zero: 'يوم',
    one: 'يوم',
    two: 'يومان',
    few: 'أيام',
    many: 'يومًا',
    other: 'يوم',
  },
  'home.recorded': 'سُجِّلت {{count}} من {{total}} صلوات اليوم',
  'home.today': 'اليوم',
  'home.now': 'الآن',
  'home.markLink': 'مواقيت اليوم — سجّل صلاة',
  'home.reward.title': {
    zero: '{{quality}}، {{count}} صلاة متتالية',
    one: '{{quality}}، صلاة واحدة متتالية',
    two: '{{quality}}، صلاتان متتاليتان',
    few: '{{quality}}، {{count}} صلوات متتالية',
    many: '{{quality}}، {{count}} صلاة متتالية',
    other: '{{quality}}، {{count}} صلاة متتالية',
  },
  'home.reward.sub': 'فانوسك في أشد لمعانه',
  'home.reward.litTitle': 'فانوسك مضيء',
  'home.reward.litSub': 'الصلوات الخمس اليوم، ويبدأ التتابع',
  'home.reward.emptyTitle': 'فانوسك ينتظر',
  'home.reward.emptySub': 'سجّل صلاة ليضيء',
  'home.hadith': 'أحب الأعمال إلى الله أدومها وإن قل.',
  'home.hadithSource': '— رواه البخاري ومسلم',

  'prayer.fajr': 'الفجر',
  'prayer.dhuhr': 'الظهر',
  'prayer.asr': 'العصر',
  'prayer.maghrib': 'المغرب',
  'prayer.isha': 'العشاء',
  // In Arabic the name and the script are the same, so the secondary line is
  // dropped rather than printed twice.
  'prayer.ar.fajr': '',
  'prayer.ar.dhuhr': '',
  'prayer.ar.asr': '',
  'prayer.ar.maghrib': '',
  'prayer.ar.isha': '',

  'note.later': 'لاحقًا اليوم',
  'note.missed': 'لم تُسجَّل — يمكنك قضاؤها',
  'note.takbirah': 'التكبيرة الأولى مع الإمام',
  'note.jamaahOnTime': 'في وقتها، وفي جماعة',
  'note.jamaahLate': 'في جماعة، بعد وقتها',
  'note.onTime': 'صُلِّيت في وقتها',
  'note.late': 'صُلِّيت بعد وقتها',

  'quality.prayed.label': 'صليتها',
  'quality.prayed.short': 'صُلِّيت',
  'quality.prayed.kids': 'الخطوة الأولى والأهم',
  'quality.prayed.adult': 'أُدِّيت الصلاة',
  'quality.onTime.label': 'في وقتها',
  'quality.onTime.short': 'في وقتها',
  'quality.onTime.kids': 'قبل أن يؤذَّن للتي بعدها',
  'quality.onTime.adult': 'ضمن الوقت المحدد',
  'quality.jamaah.label': 'في المسجد، في جماعة',
  'quality.jamaah.short': 'جماعة',
  'quality.jamaah.kids': 'كتفًا بكتف في الصفوف',
  'quality.jamaah.adult': 'في جماعة في المسجد',
  'quality.takbirah.label': 'أدركت التكبيرة الأولى',
  'quality.takbirah.short': 'التكبيرة الأولى',
  'quality.takbirah.kids': 'في الصف قبل أن يبدأ الإمام',
  'quality.takbirah.adult': 'حاضرًا لتكبيرة الإحرام',
  'quality.needsJamaah': 'تحتاج الجماعة أولًا',
  'quality.ar.prayed': '',
  'quality.ar.onTime': '',
  'quality.ar.jamaah': '',
  'quality.ar.takbirah': '',

  'log.adhan': 'الأذان {{time}}',
  'log.save': 'حفظ',
  'log.saved': 'تم الحفظ',
  'log.makeUp.kids': 'صليتها متأخرًا — قضاء',
  'log.makeUp.adult': 'تسجيلها قضاءً',
  'log.allFour.title': 'الأربع كلها، ما شاء الله',
  'log.allFour.sub': 'اكتملت علامة {{prayer}}',
  'log.saveFailed': 'لم يحفظ هذا الجهاز التسجيل. لم يضع شيء — أعد المحاولة.',
  'log.privacy': 'تبقى على هاتفك. لا يراها أحد غيرك.',
  'log.close': 'إغلاق دون تسجيل',
  'log.onDay': 'تسجيل {{day}}',

  'settings.title': 'الإعدادات',
  'settings.back': 'رجوع',
  'settings.whoIsPraying': 'من يصلي',
  'settings.name': 'الاسم',
  'settings.nameUnset': 'غير محدد',
  'settings.age': 'العمر',
  'settings.lookAndFeel': 'المظهر',
  'settings.byAge': 'اختير حسب العمر — {{age}} يحصل على النسخة {{version}}.',
  'settings.byHand': 'غُيِّر يدويًا — عمر {{age}} يحصل عادةً على النسخة {{version}}.',
  'settings.fun': 'ممتعة',
  'settings.funSub': 'فوانيس وألوان ومكافآت',
  'settings.plain': 'بسيطة',
  'settings.backToAge': 'العودة إلى النسخة {{version}} التي يختارها العمر',
  'settings.plainSub': 'السجل نفسه بلا طبقة لعب',
  'settings.lightDark': 'الفاتح والداكن',
  'settings.day': 'نهار',
  'settings.night': 'ليل',
  'settings.auto': 'تلقائي',
  'settings.autoResolved': 'تلقائي · {{scheme}}',
  'settings.autoNote':
    'يتحول التلقائي إلى النهار عند الفجر وإلى الليل عند المغرب، بحسب أوقات الصلاة نفسها.',
  'settings.themeColour': 'لون السمة',
  'settings.accentNote': 'ألوان الصفات الأربع لا تتغير',
  'settings.parentGate':
    'النسخة الممتعة لمن عمره {{age}} فأقل. يستطيع أي شخص تغييرها هنا — عن قصد، حتى لا يُمنع الطفل من شكل تطبيقه.',
  'settings.language': 'اللغة',
  'settings.prayerTimes': 'أوقات الصلاة',
  'settings.sampleTimes': 'تُعرض أوقات تجريبية حتى تحدد موقعك.',
  'settings.useLocation': 'استخدم موقعي',
  'settings.updateLocation': 'تحديث الموقع',
  'settings.locating': 'جارٍ تحديد الموقع…',
  'settings.locationDenied': 'لم تتم مشاركة الموقع، وستبقى الأوقات التجريبية.',
  'settings.locationSet': '{{lat}}, {{lon}}',
  'settings.method': 'طريقة الحساب',
  'settings.methodNote': 'تختلف المجتمعات في زوايا الشفق. اتبع مسجدك المحلي.',
  'settings.madhab': 'توقيت العصر',
  'settings.madhab.shafi': 'شافعي',
  'settings.madhab.hanafi': 'حنفي',

  'accent.brass': 'نحاسي',
  'accent.terracotta': 'طيني',
  'accent.rose': 'وردي',
  'accent.olive': 'زيتوني',
  'accent.lapis': 'لازوردي',
  'accent.emerald': 'زمردي',

  'nav.label': 'التنقّل',
  'nav.home': 'الرئيسية',
  'nav.report': 'التقرير',

  'report.previous': 'أيام سابقة',
  'report.next': 'أيام لاحقة',
  'report.theseDays': 'هذه الأيام السبعة',
  'report.recorded': '{{count}} / {{total}}',
  'report.recordedLabel': 'صلوات مسجَّلة',
  'report.fullDays': 'أيام كاملة بالخمس',
  'report.perfect': 'صلوات بالصفات الأربع',
  'report.bestRun': 'أطول سلسلة أيام كاملة',
  'report.statistics': 'إحصاءات',
  'report.rangeLabel': 'عدد الأيام التي تشملها الإحصاءات',
  'report.dayCount': {
    zero: '{{count}} يوم',
    one: 'يوم واحد',
    two: 'يومان',
    few: '{{count}} أيام',
    many: '{{count}} يومًا',
    other: '{{count}} يوم',
  },
  'report.statsNote': 'الأيام حتى اليوم، أيًّا كان الأسبوع المعروض في الشبكة أعلاه.',
  'report.statsTotal': {
    zero: 'لا صلاة فيها.',
    one: 'صلاة واحدة فيها.',
    two: 'صلاتان فيها.',
    few: '{{count}} صلوات فيها.',
    many: '{{count}} صلاة فيها.',
    other: '{{count}} صلاة فيها.',
  },
  'report.qualityShare': '{{quality}}: {{count}} من {{total}} صلاة',
  'report.eachPrayer': 'كل صلاة وكل صفة',
  'report.eachPrayerNote':
    'فوات الفجر لا يمسّ الأربع الأخرى. سلسلة كل صلاة لكل صفة من الأربع، بالأيام.',
  'report.scaleWeek': 'الأشرطة العشرون على مقياس واحد: الشريط الكامل أسبوع.',
  'report.scaleDays':
    'الأشرطة العشرون على مقياس واحد: الشريط الكامل {{count}} يومًا، وعليه علامتا الأسبوع والشهر.',
  'report.inARow': {
    zero: '{{count}} يوم متتالٍ',
    one: 'يوم واحد متتالٍ',
    two: 'يومان متتاليان',
    few: '{{count}} أيام متتالية',
    many: '{{count}} يومًا متتاليًا',
    other: '{{count}} يوم متتالٍ',
  },
  'report.longestRun': 'أطول سلسلة',
  'report.prayerQuality': '{{prayer}}، {{quality}}: {{run}}',
  'report.prayerQualityBest': '{{prayer}}، {{quality}}: {{run}}، وأطولها {{best}}',
  'report.starNote': 'النجمة على الشريط هي أطول ما بلغته تلك السلسلة.',
  'report.empty': 'لم يُسجَّل شيء في هذه الأيام بعد.',
  'report.legend': 'كل علامة تُظهر الصفات الأربع: صُلِّيت، في وقتها، في جماعة، التكبيرة الأولى.',
  'report.editHint': 'اضغط على أي صلاة لتسجيلها، في أي من هذه الأيام.',
  'report.cellLabel': '{{prayer}}، {{day}}: {{note}}',
};
